/**
 * Pie / Donut Chart Renderer
 *
 * Creates an amCharts 5 PieChart from a PieDonutConfig.
 * The donut variant uses innerRadius > 0 and supports a center label
 * showing a sum or custom text.
 */

import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';

import type { PieDonutConfig } from '../../../../types/dashboard';
import { DASHBOARD_TOKENS } from '../tokens';
import { formatCurrency, formatNumber } from '../formatters';

export function buildPieDonutChart(
  root: am5.Root,
  config: PieDonutConfig,
  data: Record<string, unknown>[],
  isDonut: boolean
): am5percent.PieChart {
  const chart = root.container.children.push(
    am5percent.PieChart.new(root, {
      layout: root.verticalLayout,
      innerRadius: isDonut ? am5.percent(config.innerRadius ?? 50) : 0,
    })
  );

  const series = chart.series.push(
    am5percent.PieSeries.new(root, {
      valueField: config.valueField,
      categoryField: config.categoryField,
    })
  );

  if (config.colors && config.colors.length > 0) {
    series.set(
      'colors',
      am5.ColorSet.new(root, {
        colors: config.colors.map((c) => am5.color(c)),
      })
    );
  } else {
    series.set(
      'colors',
      am5.ColorSet.new(root, {
        colors: DASHBOARD_TOKENS.palette.map((c) => am5.color(c)),
      })
    );
  }

  series.slices.template.setAll({
    cornerRadius: config.visual?.cornerRadius ?? DASHBOARD_TOKENS.cornerRadius,
    strokeOpacity: 0,
    tooltipText: '{category}: {value}',
  });

  // Labels
  if (config.visual?.labels !== false) {
    series.labels.template.setAll({
      fill: am5.color(DASHBOARD_TOKENS.textSecondary),
      fontSize: DASHBOARD_TOKENS.fontSizeSmall,
      maxWidth: config.visual?.labelsMaxWidth ?? 120,
      oversizedBehavior: 'truncate',
    });
  } else {
    series.labels.template.set('visible', false);
    series.ticks.template.set('visible', false);
  }

  // Center label for donut charts
  if (isDonut && config.centerLabel?.show) {
    const centerContainer = chart.seriesContainer.children.push(
      am5.Container.new(root, {
        centerX: am5.percent(50),
        centerY: am5.percent(50),
        x: am5.percent(50),
        y: am5.percent(50),
        layout: root.verticalLayout,
      })
    );

    if (config.centerLabel.topText) {
      centerContainer.children.push(
        am5.Label.new(root, {
          text: config.centerLabel.topText,
          fill: am5.color(DASHBOARD_TOKENS.textMuted),
          fontSize: DASHBOARD_TOKENS.fontSizeSmall,
          centerX: am5.percent(50),
          x: am5.percent(50),
          textAlign: 'center',
        })
      );
    }

    if (config.centerLabel.showSum) {
      const sum = data.reduce((acc, row) => {
        const val = Number(row[config.valueField]) || 0;
        return acc + val;
      }, 0);

      const formattedSum =
        config.centerLabel.sumFormat === 'currency'
          ? formatCurrency(sum, config.centerLabel.currency)
          : formatNumber(sum);

      centerContainer.children.push(
        am5.Label.new(root, {
          text: formattedSum,
          fill: am5.color(DASHBOARD_TOKENS.textPrimary),
          fontSize: 20,
          fontWeight: '600',
          centerX: am5.percent(50),
          x: am5.percent(50),
          textAlign: 'center',
        })
      );
    }
  }

  // Legend
  if (config.visual?.legend !== false) {
    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        marginTop: 10,
      })
    );
    legend.labels.template.setAll({
      fill: am5.color(DASHBOARD_TOKENS.textSecondary),
      fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    });
    legend.valueLabels.template.set('visible', false);
    legend.data.setAll(series.dataItems);
  }

  series.data.setAll(data);
  series.appear();

  return chart;
}
